// Admin editor for the dashboard's pinned boxes. Tick which boxes appear on everyone's Dashboard
// page; each box is still gated by the viewer's own permission, so enabling one never leaks data to
// someone who couldn't otherwise see it. The enabled set is stored once (config 'dashboard.boxes')
// and delivered to every client on /me.

import { useEffect, useMemo, useState } from 'react';
import { DASHBOARD_BOXES, DEFAULT_DASHBOARD_BOXES, normalizeDashboardBoxes } from '@technohull/shared';
import { ActionBar, ErrorState, InlineError, LoadingState, PageHeader } from '../../components/ui.js';
import { useAdminConfig, useSetConfig } from '../../features/admin.js';
import { AdminBack } from './AdminHub.js';

const GROUP_ORDER = ['Statistics', 'Operations', 'Personal'] as const;
const GROUP_HINT: Record<string, string> = {
  Statistics: 'Headline production figures',
  Operations: 'Day-to-day lists and what is coming up',
  Personal: 'Specific to the signed-in user',
};

export function AdminDashboard() {
  const { data, isLoading, error, refetch } = useAdminConfig();
  const save = useSetConfig();

  const [sel, setSel] = useState<Set<string> | null>(null);

  // seed the local selection from the saved config the first time it arrives.
  useEffect(() => {
    if (data && sel === null) setSel(new Set(normalizeDashboardBoxes(data.dashboardBoxes)));
  }, [data, sel]);

  const saved = useMemo(() => normalizeDashboardBoxes(data?.dashboardBoxes ?? DEFAULT_DASHBOARD_BOXES), [data]);
  const current = sel ?? new Set(saved);
  // how many boxes differ from the saved set (switched on + switched off)
  const changes = useMemo(() => {
    const was = new Set(saved);
    return [...current].filter((id) => !was.has(id)).length + saved.filter((id) => !current.has(id)).length;
  }, [current, saved]);
  const dirty = changes > 0;

  const toggle = (id: string) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSel(next);
  };
  const setAll = (on: boolean) => setSel(on ? new Set(DASHBOARD_BOXES.map((b) => b.id)) : new Set());

  const submit = () =>
    save.mutate(
      { dashboardBoxes: normalizeDashboardBoxes([...current]) },
      { onSuccess: () => setSel(new Set(normalizeDashboardBoxes([...current]))) },
    );

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <AdminBack />
      <PageHeader title="Dashboard">
        <button className="btn btn-sm btn-ghost" disabled={current.size === DASHBOARD_BOXES.length} onClick={() => setAll(true)}>Enable all</button>
        <button className="btn btn-sm btn-ghost" disabled={current.size === 0} onClick={() => setAll(false)}>Disable all</button>
        <button className="btn btn-sm btn-primary" onClick={submit} disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
        </button>
      </PageHeader>
      {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}

      {GROUP_ORDER.map((group) => {
        const boxes = DASHBOARD_BOXES.filter((b) => b.group === group);
        if (!boxes.length) return null;
        return (
          <section key={group} className="adm-group">
            <div className="adm-group-hd">
              <h2 className="adm-group-title">{group}</h2>
              <span className="adm-group-hint">{GROUP_HINT[group]}</span>
            </div>
            <div className="page-panel adm-dash" style={{ display: 'grid', gap: 4 }}>
              {boxes.map((b) => (
                <label key={b.id} className="perm-switch" style={{ alignItems: 'flex-start', padding: '8px 4px' }}>
                  <input type="checkbox" checked={current.has(b.id)} onChange={() => toggle(b.id)} />
                  <span style={{ display: 'grid', gap: 2 }}>
                    <span style={{ fontWeight: 600 }}>{b.label}</span>
                    <span className="muted" style={{ fontSize: 12 }}>{b.desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>
        );
      })}
      {/* phones: the twelve switches run well past the header's Save, so it rides at the bottom too */}
      <ActionBar show={dirty} message={`${changes} change${changes === 1 ? '' : 's'}`}>
        <button className="btn btn-primary" disabled={save.isPending} onClick={submit}>{save.isPending ? 'Saving…' : 'Save'}</button>
      </ActionBar>
    </>
  );
}
